import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  CheckCircleIcon,
  EyeIcon,
  ScaleIcon,
  LockClosedIcon,
  ArrowPathIcon,
  ChartBarIcon,
  DocumentCheckIcon,
  BuildingLibraryIcon,
  ClipboardDocumentCheckIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

/* ============================================================
   Duane Morris (Joseph E. Silvia) × Clarid — relationship page.
   Standalone, noindex, sent directly to Mr. Silvia. Grounded in
   verified research (2026-07-21). Guards: it is the AMERICAN BAR
   ASSOCIATION (Business Law Section) Banking Law Committee — NOT
   American Bankers Association; his federal background is the
   FEDERAL RESERVE Bank of Chicago (not FDIC/OCC); no claim he has
   authored on fair lending / AI / CRA specifically. Clarid is
   positioned as the execution layer BENEATH counsel — it does not
   give legal advice. BSA/AML is only an NDA-safe roadmap allusion.
   ============================================================ */

const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}
function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}
function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

const capabilities = [
  { icon: EyeIcon, title: 'Marketing Compliance', description: 'Every ad, email, social post, and disclosure checked against federal rules (Reg Z, UDAAP, TILA, FTC Act) with AI detection, a deterministic rules engine, and mandatory human approval — examiner-ready on every check.' },
  { icon: DocumentCheckIcon, title: 'HMDA', description: 'LAR validation with 160+ edit checks, ML-assisted LOS imports, geocoding, data-quality scoring, and submission-ready exports.' },
  { icon: ScaleIcon, title: 'Fair Lending', description: 'Denial and pricing-disparity testing, redlining with census-tract mapping, and formal risk assessments — tuned to FDIC, OCC, Fed, and NCUA examination standards.' },
  { icon: BuildingLibraryIcon, title: 'CRA', description: 'Assessment-area construction, lending-distribution analysis, peer benchmarking, and examiner-ready performance-evaluation tables.' },
  { icon: ChartBarIcon, title: 'Regression & Statistical Testing', description: 'Logistic and OLS regression and matched-pair analysis — regulator-aware and validated for model soundness under the 2026 interagency model risk guidance (SR 26-2 / OCC Bulletin 2026-13).' },
  { icon: ClipboardDocumentCheckIcon, title: 'Exam & Board Packaging', description: 'Findings from every module assemble into one examiner-ready package and a board-ready summary — timestamped and hash-verified.' },
];

const governance = [
  { icon: CheckCircleIcon, title: 'Human-in-the-Loop by Design', description: 'AI performs detection, a deterministic rules engine makes the compliance determination, and a person makes the final decision. Every AI-generated finding is reviewed and approved through role-based workflows. No autonomous compliance decisions.' },
  { icon: LockClosedIcon, title: 'Hash-Chained Audit Trail', description: 'Every check, finding, approval, and override is recorded in a SHA-256 hash-chained, append-only log — tampering is mathematically detectable. Timestamped, attributed to a user and a model, and examiner-exportable.' },
  { icon: ChartBarIcon, title: 'Model Validation & Bias Testing', description: 'Formal model validation against curated fixtures, plus bias testing across protected-class dimensions using Fisher\'s exact test — consistent with NIST AI 100-1 and the 2026 interagency model risk guidance (SR 26-2 / OCC Bulletin 2026-13, which superseded SR 11-7).' },
  { icon: ArrowPathIcon, title: 'Multi-Provider Resilience', description: 'Three foundation-model providers with a circuit-breaker and automatic failover. No single-vendor dependency; model selection is rule-based, deterministic, and auditable.' },
];

const avenues = [
  { num: '01', title: 'The Execution Layer Beneath Counsel', description: "Duane Morris advises roughly 200 banks and a roster of fintechs on the obligations that fill a compliance calendar. Clarid is the technology that executes and sustains those programs continuously between engagements — giving the institutions you advise provable, examiner-ready compliance. It operationalizes what counsel recommends; it does not give legal advice." },
  { num: '02', title: 'A Responsible-AI Reference Point', description: "As Chair of the American Bar Association's Banking Law Committee and a professor of fintech regulation, you sit where the profession is working out how AI should be governed inside regulated banking. Clarid is a working, inspectable example of that done responsibly — human-in-the-loop, deterministic determinations, full auditability — a concrete reference for the conversations you help lead." },
  { num: '03', title: 'A Complement, Not a Competitor', description: "Clarid never renders a legal opinion. It makes your bank and fintech clients more defensible on the day an examiner arrives — which makes your advice land, and stick. That non-competitive framing is the whole point: the platform sits beneath the counsel, not beside it." },
];

export default function DuaneMorrisPartnerPage() {
  useEffect(() => {
    document.title = 'Duane Morris × Clarid — An Introduction for Joseph E. Silvia';
    const metaTags: Record<string, string> = {
      'og:title': 'Duane Morris × Clarid',
      'og:description': 'The AI-native, examiner-grade compliance platform for the community banks and fintechs Duane Morris advises. Built by a community-bank CMO, validated by former FDIC and Federal Reserve examiners.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'robots': 'noindex, nofollow',
    };
    const created: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute(property === 'robots' ? 'name' : 'property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      created.push(meta);
    }
    return () => {
      created.forEach((t) => document.head.removeChild(t));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* Header · lockup */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
            <span className="text-slate-300 text-xl font-light">×</span>
            <span className="font-display text-2xl font-bold text-navy-900 tracking-tight">Duane Morris</span>
          </div>
        </div>
      </header>

      {/* 1 · Hero */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp><p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-8">Prepared for Joseph E. Silvia</p></FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              The execution layer beneath the counsel you give
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid is an AI-native compliance operating system for community banks and credit unions — built by a
              community-bank CMO, validated by a former FDIC Supervisory Examiner and a former Federal Reserve
              fair-lending subject-matter expert. It operationalizes the regulatory obligations you and Duane Morris
              advise on. It does not give legal advice.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* 2 · The fit */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                Advice sets the standard. Something has to run it every day.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  You spent years as counsel to the Federal Reserve Bank of Chicago and now advise financial institutions
                  and fintechs at Duane Morris — a firm that represents roughly 200 banks nationally. You know, first-hand,
                  the distance between sound regulatory advice and a bank actually running it, day after day, in a way an
                  examiner will credit.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Clarid closes that distance. It executes and sustains the consumer-compliance program between engagements —
                  marketing review, HMDA, fair lending, CRA, and one-click exam- and board-ready packaging — turning
                  point-in-time advice into continuous, provable compliance for the institutions you counsel.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  It is the layer beneath the counsel — complementary to your advice, never a substitute for it.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* 3 · Capabilities */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
              What the platform does
            </h2>
            <p className="text-lg text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
              The consumer-compliance and fair-lending work community banks manage and examiners scrutinize.
              (Clarid is a consumer-compliance platform today; BSA/AML — an area of your own deep practice — is a
              deliberate next chapter on our roadmap.)
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · AI governance */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '48px 48px' }} />
        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-300 mb-5">Regulatory Rigor, Not AI Hype</p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                AI in banking, governed the way the profession is asking for
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                The question you and your committee keep returning to is not whether AI enters bank compliance, but how it is
                governed when it does. Clarid was built with examiner defensibility as its first principle — a working,
                inspectable example of responsible AI in a regulated workflow.
              </p>
            </div>
          </FadeUp>
          <div className="space-y-6 lg:space-y-8 mt-14">
            {governance.map((c, i) => (
              <ScaleIn key={c.title} delay={i * 0.08}>
                <div className="p-10 lg:p-12 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500">
                  <div className="flex items-center gap-4 mb-5">
                    <c.icon className="h-7 w-7 text-teal-400 flex-shrink-0" />
                    <h3 className="text-2xl font-display font-bold text-white">{c.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed">{c.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* 5 · Built by the buyer / validated by examiners */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">Built with the examination perspective</h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-6xl mx-auto">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">Validated by former examiners</h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve fair-lending subject-matter expert validate
                  Clarid's rules, workflows, and outputs against what examiners actually evaluate — with decades of combined
                  experience across hundreds of community-bank examinations.
                </p>
              </div>
            </ScaleIn>
            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">Built by the buyer</h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Clarid's founder is a community-bank CMO who manages marketing compliance, HMDA reporting, and examiner
                  relationships as part of his daily work — not engineers guessing at compliance needs. Its security and
                  governance posture (tenant isolation, documented AI governance, SOC 2 in progress) is built to survive the
                  diligence a bank regulatory lawyer would run.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* 6 · Where it could go */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">Where this could go</h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                Three threads worth a conversation. Any one is a starting point.
              </p>
            </FadeUp>
            <div className="space-y-6 lg:space-y-8">
              {avenues.map((a) => (
                <ScaleIn key={a.num} delay={0.05}>
                  <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500">
                    <div className="flex items-start gap-6">
                      <span className="text-3xl font-display font-bold text-navy-900/20 flex-shrink-0 mt-0.5">{a.num}</span>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-navy-900 mb-3">{a.title}</h3>
                        <p className="text-lg text-slate-500 leading-relaxed">{a.description}</p>
                      </div>
                    </div>
                  </div>
                </ScaleIn>
              ))}
            </div>
            <FadeUp delay={0.3}>
              <p className="mt-12 text-center text-lg text-slate-500 italic">
                We would welcome your read on where Clarid is most useful — to the institutions you advise, and to the
                standards conversation you help lead.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* 7 · Contact */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-6">Thank you for your time, Joe</p>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder &amp; CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-teal-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-teal-700 transition-colors">bobby@clarid.ai</a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-slate-400">
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
            <span className="text-slate-200">×</span>
            <span className="font-display font-semibold text-sm">Duane Morris</span>
          </div>
          <p className="text-slate-400 text-sm">&copy; {new Date().getFullYear()} Clarid. Confidential — prepared for Joseph E. Silvia.</p>
          <div className="flex items-center gap-4 text-sm">
            <a href="/security" className="text-slate-400 hover:text-teal-600 transition-colors">Security</a>
            <a href="/privacy" className="text-slate-400 hover:text-teal-600 transition-colors">Privacy</a>
          </div>
        </div>
      </footer>

      <div className="sr-only">
        Prepared by Clarid for Joseph E. Silvia of Duane Morris LLP. This page is an introduction from Clarid and does not
        imply an existing relationship with, or endorsement by, Mr. Silvia, Duane Morris LLP, or the American Bar Association.
      </div>
    </div>
  );
}
